import { Hit } from "instantsearch.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useClearRefinements,
  useConfigure,
  useInfiniteHits,
  useInstantSearch,
  useRefinementList,
} from "react-instantsearch-core-v7";
import { DeprecatedAlgoliaSearchProduct } from "src/types/Algolia";
import {
  BRAND_DESIGNER_ONLY_FACET,
  BRAND_IS_PRIVATE_SALE,
  CATEGORIES_FACET,
  CATEGORY_PAGE_ID_FACET,
  GENDERS_FACET,
  IS_DELETED,
  LABELS_TYPE_FACET,
  PRICE_FACET,
} from "src/utils/algolia";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { useRefineCategoryPageId } from "./useRefineCategoryPageId";
import { PLPType } from "src/types/PLPType.d";
import { BaseHit } from "instantsearch.js/es/types";
import contextKeys, { listTypes } from "src/utils/algolia/contextKeys";
import { WEB_SHOP } from "src/utils/algolia/analyticsTags";
import { useRouter } from "next/router";
import {
  queryStringKeyMap,
  queryStringValuePosMap,
} from "src/utils/handle-search-params";
import { availableFiltersList } from "src/organisms/AlgoliaProductList/availableFilters";
import { SEGMENT_LIST_FORMAT } from "src/segment";
import { LimitType, useSegmentProductList } from "../useSegmentProductList";
import {
  CatalogProduct,
  HomepageSingleCategoryProduct,
} from "src/types/graphql.d";
import { useSession } from "next-auth/client";

export type UseProductListingProps = {
  title?: string;
  gender: string;
  categoryPageId: string;
  hitsPerPage: number;
  pageType: PLPType;
  indexName: string;
};

export type UseProductListingResult = {
  isLoading: boolean;
  products: Hit<DeprecatedAlgoliaSearchProduct & BaseHit>[];
  total: number;
  hasNoResults: boolean;
  showMoreProducts: () => void;
  segmentProductOnClick: (
    product: (CatalogProduct | HomepageSingleCategoryProduct) & {
      objectID?: string;
    },
    position: number
  ) => void;
  setVisibleProducts: (
    product: CatalogProduct | HomepageSingleCategoryProduct,
    position: number,
    limitType?: LimitType
  ) => void;
};

function useProductListing({
  gender,
  categoryPageId,
  hitsPerPage,
  pageType,
  indexName,
  title,
}: UseProductListingProps): UseProductListingResult {
  const categoryPageIdRef = useRef<string>(categoryPageId);
  const nbHitsRef = useRef(0);
  const areFiltersLoaded = useRef(false);
  const [session, isSessionLoading] = useSession();

  const router = useRouter();

  let filters = `${GENDERS_FACET}:${localeFix({
    gender,
  })} AND ${IS_DELETED}:false AND ${BRAND_IS_PRIVATE_SALE}:false`;

  if (pageType === PLPType.NEW_IN) {
    filters += ` AND ${LABELS_TYPE_FACET}:new`;
  }

  if (pageType !== PLPType.NEW_IN) {
    filters += ` AND ${CATEGORY_PAGE_ID_FACET}:"${categoryPageId}"`;
  }

  if (pageType !== PLPType.BRAND_PLP) {
    filters += ` AND ${BRAND_DESIGNER_ONLY_FACET}:false`;
  }

  const { refine } = useConfigure({
    advancedSyntax: true,
    filters,
    facets: ["*"],
    hitsPerPage,
    maxValuesPerFacet: 1000,
    getRankingInfo: true,
    ruleContexts: [WEB_SHOP, contextKeys[pageType]],
    analyticsTags: [WEB_SHOP, localeFix({ gender })],
    clickAnalytics: true,
    enablePersonalization: isSessionLoading || !session ? false : true,
    userToken:
      isSessionLoading || !session ? undefined : session.user.member_id,
  });

  useRefinementList({ attribute: CATEGORY_PAGE_ID_FACET });

  const [isLoading, setLoading] = useState(false);
  const { results, status, indexUiState, setIndexUiState, indexRenderState } =
    useInstantSearch();
  const { refine: clear } = useClearRefinements();
  const { items, showMore } = useInfiniteHits<DeprecatedAlgoliaSearchProduct>();
  const isLoadingComplete = useMemo(() => {
    const isLoading =
      !results.__isArtificial &&
      nbHitsRef.current !== results.nbHits &&
      status !== "stalled" &&
      status !== "loading";

    nbHitsRef.current = results.nbHits;

    return isLoading;
  }, [results.__isArtificial, results.nbHits, status]);

  useRefineCategoryPageId({
    mainCategoryPageId: categoryPageId,
    setIndexUiState,
    indexUiState,
    refine,
    isLoadingComplete,
    initialFilters: filters,
  });

  useEffect(() => {
    if (categoryPageIdRef.current !== categoryPageId) {
      categoryPageIdRef.current = categoryPageId;
      clear();
    }
  }, [categoryPageId, clear]);

  useEffect(() => {
    if (status !== "idle" && status !== "error") {
      setLoading(true);

      return;
    }

    setLoading(false);
  }, [status, isLoading]);

  const { segmentProductOnClick, setVisibleProducts } = useSegmentProductList({
    algoliaIndex: indexName,
    listFormat: SEGMENT_LIST_FORMAT.default,
    listId: title || "",
    listType: listTypes[pageType],
    queryID: results.queryID,
    numberOfItems: results.nbHits,
    shopType: localeFix({ gender }),
  });

  const handleInitialRefinements = useCallback(() => {
    if (!indexRenderState.refinementList) {
      return;
    }

    const refinementList = indexRenderState.refinementList;

    const initialRefinements: Record<string, string[]> = {};
    const url = new URL(location.toString());
    const selectedCategorySlugs = url.searchParams.getAll(
      queryStringKeyMap[CATEGORIES_FACET]
    );

    const selectedCategories: {
      itemId: string;
      slug: string;
      parentId: string;
    }[] =
      refinementList[CATEGORIES_FACET].items.flatMap((item) => {
        const splitVal = item.value.split("||");

        if (!selectedCategorySlugs.includes(splitVal[2])) {
          return [];
        }

        return [
          {
            itemId: splitVal[0],
            slug: splitVal[2],
            parentId: splitVal[3],
          },
        ];
      }) || [];

    const parentCategories = selectedCategories.flatMap((item) =>
      selectedCategories.some(
        (parentItem) => parentItem.parentId === item.itemId
      )
        ? [item.slug]
        : []
    );

    availableFiltersList
      .filter(({ facet }) => facet !== PRICE_FACET)
      .forEach(({ facet }) => {
        const items = url.searchParams.getAll(queryStringKeyMap[facet]);
        const isCategory = facet === CATEGORIES_FACET;

        const selectedItems =
          refinementList[facet]?.items.flatMap((item) => {
            const splitVal = item.value.split("||");

            if (parentCategories.includes(splitVal[2])) {
              return [];
            }

            return items.includes(
              splitVal[isCategory ? 2 : queryStringValuePosMap[facet]]
            )
              ? [
                  isCategory
                    ? (splitVal.at(queryStringValuePosMap[facet]) as string)
                    : item.value,
                ]
              : [];
          }) || [];

        if (selectedItems.length > 0) {
          initialRefinements[isCategory ? CATEGORY_PAGE_ID_FACET : facet] =
            selectedItems;
        }
      });

    setIndexUiState((prevState) => ({
      ...prevState,
      refinementList: {
        ...prevState.refinementList,
        ...initialRefinements,
      },
    }));
  }, [indexRenderState, setIndexUiState]);

  useEffect(() => {
    router.events.on("routeChangeComplete", handleInitialRefinements);

    return () => {
      router.events.off("routeChangeComplete", handleInitialRefinements);
    };
  }, [router.events, handleInitialRefinements]);

  useEffect(() => {
    if (
      status === "loading" ||
      status === "stalled" ||
      results.__isArtificial ||
      areFiltersLoaded.current ||
      !indexRenderState.refinementList
    ) {
      return;
    }

    handleInitialRefinements();

    areFiltersLoaded.current = true;
  }, [
    indexRenderState,
    results.__isArtificial,
    status,
    handleInitialRefinements,
  ]);

  const values = useMemo(
    () => ({
      products: items,
      showMoreProducts: showMore,
      isLoading,
      total: results.nbHits,
      hasNoResults: !results.__isArtificial && results.nbHits === 0,
      segmentProductOnClick,
      setVisibleProducts,
    }),
    [
      items,
      showMore,
      isLoading,
      results.nbHits,
      results.__isArtificial,
      segmentProductOnClick,
      setVisibleProducts,
    ]
  );

  return values;
}

export { useProductListing };
